<template>
	<transition
		name="dropzone"
		enter-active-class="opacity-enter-active"
		leave-active-class="opacity-leave-active"
		enter-class="opacity-enter"
		leave-to-class="opacity-leave-to"
	>
		<div
			class="dropzone-area-wrapper"
			:class="{ scoped }"
			:style="
				extendTop
					? { top: `-${extendTop}px`, height: `calc(100% + ${extendTop}px)` }
					: {}
			"
			v-show="dragging"
		>
			<div
				class="dropzone-area"
				@dragenter="onOverChange(true)"
				@dragover="onOverChange(true)"
				@dragleave="onOverChange(false)"
				@drop.prevent="onDrop"
			>
				<div class="dropzone-border" :class="{ over }">
					<div class="content" style="pointer-events: none">
						<CloudArrowUpIcon type="solid" />
						<div v-t="over ? 'chat_file_drop' : 'chat_file_drag'" />
					</div>
				</div>
				<input type="file" multiple @change.prevent.stop="onDrop" />
			</div>
		</div>
	</transition>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { CloudArrowUpIcon } from '@/icons'
import { useEventBus } from '@/helpers/bus'

const props = defineProps({
	// When true, uses absolute positioning within container instead of fixed full-page overlay
	scoped: { type: Boolean, default: false },
	// Pixels to extend the dropzone above its container (for covering headers outside the container)
	extendTop: { type: Number, default: 0 },
})
const emit = defineEmits(['over', 'off', 'drop', 'dragging'])

const dragging = ref(false)
const over = ref(false)
let isEnabled = true

watch(over, () => emit(over.value ? 'over' : 'off'))
watch(dragging, () => emit('dragging', dragging.value))

useEventBus('toggleDragZone', (enabled = true) => {
	isEnabled = enabled
})

onMounted(() => {
	// Always listen at document level to show dropzone when ANY drag starts
	document.addEventListener('dragenter', onDragEnter)
	document.addEventListener('dragover', onDragEnter)

	// Document-level to know when dragging ends anywhere
	document.addEventListener('drop', onDragEnd)
	document.addEventListener('dragend', onDragEnd)
	document.addEventListener('dragleave', onDocumentLeave)
})

onUnmounted(() => {
	document.removeEventListener('dragenter', onDragEnter)
	document.removeEventListener('dragover', onDragEnter)
	document.removeEventListener('drop', onDragEnd)
	document.removeEventListener('dragend', onDragEnd)
	document.removeEventListener('dragleave', onDocumentLeave)
})

function onOverChange(isOver) {
	over.value = isEnabled ? isOver : false
}

function onDragEnter() {
	// Show dropzone when dragging starts anywhere
	dragging.value = isEnabled ? true : false
}

function onDragEnd() {
	// Clear everything when drop happens anywhere or drag is cancelled
	dragging.value = false
	over.value = false
}

function onDocumentLeave(e) {
	// Detect when drag leaves the browser window entirely
	// This happens when relatedTarget is null and we're at the viewport edge
	if (
		e.clientX <= 0 ||
		e.clientY <= 0 ||
		e.clientX >= window.innerWidth ||
		e.clientY >= window.innerHeight
	) {
		dragging.value = false
		over.value = false
	}
}

function onDrop(e) {
	dragging.value = false
	over.value = false
	emit('drop', e)
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.dropzone-area-wrapper {
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	@include max-z;
	position: fixed;
	background: rgba(0, 0, 0, 0.39);

	.dropzone-area {
		top: 45px;
		left: 280px;
		right: 0;
		bottom: 0;
		z-index: 2;
		position: absolute;
		background-color: rgba(255, 255, 255, 0.9);
		border-top-left-radius: 10px;
		border-bottom-left-radius: 10px;
		padding: 15px;

		// When thread panel is open, don't overlap it (only for non-scoped/fixed mode)
		.thread-open & {
			right: var(--thread-width, 408px);
			border-radius: 10px;
		}

		input[type='file'] {
			position: absolute;
			cursor: pointer;
			top: 0px;
			right: 0;
			bottom: 0;
			left: 0;
			width: 100%;
			height: 100%;
			opacity: 0;
		}

		.dropzone-border {
			@include flex-column-center;
			justify-content: center;
			height: 100%;
			width: 100%;
			border-radius: 10px;
			border: 4px dashed var(--neutral-500);

			&.over {
				color: var(--teal-300);
				border-color: var(--teal-300);
				border-style: solid;
			}

			.content {
				@include flex-column-center(8px);
				z-index: 5;
				text-align: center;
				width: 300px;
				font-size: 20px;

				.pronto-icon {
					font-size: 120px;
				}
			}
		}
	}

	// Scoped mode: fill container completely
	&.scoped {
		position: absolute;
		background: rgba(0, 0, 0, 0.25);

		.dropzone-area {
			left: 0;
			right: 0;
			top: 0;
			bottom: 0;
			border-radius: 8px;
		}
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.dropzone-area-wrapper {
		.dropzone-area {
			background-color: rgba(100, 100, 100, 0.8);
		}
	}
}
</style>
